// Google Translate website-translator integration: loader, cookie handling, React DOM guard.
const KEY = "dropit-lang";
const COOKIE = "googtrans";
const listeners = new Set();
let status = "idle"; // idle | loading | ready | unavailable

const emit = () => listeners.forEach((fn) => fn(getState()));
export const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

export const savedLanguage = () => localStorage.getItem(KEY) || "en";
export const hasSavedLanguage = () => localStorage.getItem(KEY) !== null;
export const getState = () => ({ status, lang: savedLanguage() });

// Contractor pages (/v/:token) have built-in dictionaries for these codes → no Google needed.
export const BUILTIN = ["en", "es", "fr", "de", "pt", "it", "zh", "hi", "tl", "mi"];
export const isContractorPage = () => window.location.pathname.startsWith("/v/");
export const builtinCode = (code) => { const b = (code || "en").split("-")[0]; return BUILTIN.includes(b) ? b : null; };
const usesBuiltin = (code) => isContractorPage() && !!builtinCode(code);
const googleDomActive = () => !!document.querySelector("font.VIpgJd-yAWNEb-VIpgJd-fmcmS") || document.documentElement.lang !== "en";

// React + Google Translate guard: GT wraps text nodes in <font>, so React may try to remove/insert
// children whose parent changed. Make those two operations tolerant instead of throwing.
const pinBodyTop = () => {
  const reset = () => { if (document.body.style.top && document.body.style.top !== "0px") document.body.style.top = "0px"; if (document.body.style.position === "relative") document.body.style.position = ""; };
  new MutationObserver(reset).observe(document.body, { attributes: true, attributeFilter: ["style", "class"] });
  reset();
};

export const installDomGuard = () => {
  if (document.body) pinBodyTop(); else document.addEventListener("DOMContentLoaded", pinBodyTop, { once: true });
  if (typeof Node === "undefined" || Node.prototype.__dropitGuard) return;
  const origRemove = Node.prototype.removeChild;
  const origInsert = Node.prototype.insertBefore;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) { console.warn("[gt-guard] removeChild on detached node skipped"); return child; }
    return origRemove.call(this, child);
  };
  Node.prototype.insertBefore = function (newNode, ref) {
    if (ref && ref.parentNode !== this) { console.warn("[gt-guard] insertBefore with foreign ref → append"); return this.appendChild(newNode); }
    return origInsert.call(this, newNode, ref);
  };
  Node.prototype.__dropitGuard = true;
};

const rootDomain = () => { const h = window.location.hostname; const parts = h.split("."); return parts.length > 2 ? "." + parts.slice(-2).join(".") : h; };
const setCookie = (value) => {
  const exp = value ? "; max-age=31536000" : "; max-age=0";
  document.cookie = `${COOKIE}=${value || ""}; path=/${exp}`;
  document.cookie = `${COOKIE}=${value || ""}; path=/; domain=${window.location.hostname}${exp}`;
  document.cookie = `${COOKIE}=${value || ""}; path=/; domain=${rootDomain()}${exp}`;
};

const combo = () => document.querySelector(".goog-te-combo");

const applyToWidget = (code) => {
  const el = combo();
  if (!el) return false;
  if (code === "en") {
    const back = document.querySelector(".goog-te-banner-frame")?.contentDocument?.querySelector(".goog-close-link");
    if (back) { back.click(); return true; }
  }
  el.value = code === "en" ? "" : code;
  el.dispatchEvent(new Event("change", { bubbles: true }));
  return true;
};

export const loadScript = () => {
  if (status === "ready" || status === "loading") return;
  if (!navigator.onLine) { status = "unavailable"; emit(); return; }
  status = "loading"; emit();
  let host = document.getElementById("google_translate_element");
  if (!host) { host = document.createElement("div"); host.id = "google_translate_element"; host.className = "gt-host notranslate"; host.setAttribute("aria-hidden", "true"); host.setAttribute("translate", "no"); document.body.appendChild(host); }
  window.googleTranslateElementInit = () => {
    try {
      new window.google.translate.TranslateElement({ pageLanguage: "en", autoDisplay: false, includedLanguages: "" }, "google_translate_element");
      status = "ready"; emit();
    } catch (e) { console.warn("gt init failed", e); status = "unavailable"; emit(); }
  };
  const s = document.createElement("script");
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  s.onerror = () => { status = "unavailable"; emit(); };
  document.head.appendChild(s);
  setTimeout(() => { if (status === "loading") { status = "unavailable"; emit(); } }, 8000);
};

// Select a language: persist, set cookie, drive the hidden widget (or reload as a fallback).
export const setLanguage = async (code) => {
  localStorage.setItem(KEY, code);
  document.documentElement.setAttribute("data-lang", code);
  if (usesBuiltin(code)) {  // contractor dictionary: instant + offline, Google stays off for this page
    setCookie("");
    emit();
    if (googleDomActive()) window.location.reload();
    return;
  }
  setCookie(code === "en" ? "" : `/en/${code}`);
  emit();
  if (code === "en") {  // the widget's "show original" is unreliable → reload without the cookie restores the untouched DOM
    if (googleDomActive()) window.location.reload();
    return;
  }
  if (status !== "ready") loadScript();
  const ok = await waitFor(() => status === "ready" && combo(), 6000);
  if (!ok || !applyToWidget(code)) { if (status === "unavailable" && code !== "en") return; window.location.reload(); }
};

const waitFor = (pred, ms) => new Promise((res) => { const t0 = Date.now(); const tick = () => (pred() ? res(true) : Date.now() - t0 > ms ? res(false) : setTimeout(tick, 100)); tick(); });

// On boot: only load the widget if the user has a saved non-English choice (never auto-translate first visits).
export const bootTranslate = () => {
  installDomGuard();
  const saved = savedLanguage();
  document.documentElement.setAttribute("data-lang", saved);
  if (usesBuiltin(saved)) { setCookie(""); return; }
  if (saved !== "en") {
    setCookie(`/en/${saved}`);
    const start = () => loadScript();
    if ("requestIdleCallback" in window) window.requestIdleCallback(start, { timeout: 1500 }); else setTimeout(start, 300);
    window.addEventListener("online", () => { if (status === "unavailable") { status = "idle"; loadScript(); } });
  }
};

/* Google's widget never queues the Radix dialog subtree on insertion, but it does translate characterData mutations.
   Re-assigning each text node to itself inside `root` makes the widget (re)translate it. No-op when not translating. */
export const retranslate = (root) => {
  if (!root || savedLanguage() === "en" || usesBuiltin(savedLanguage())) return;
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (n.nodeValue.trim() && !n.parentElement?.closest(".notranslate,[translate=no]") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT) });
  let n; while ((n = w.nextNode())) n.nodeValue = n.nodeValue;  // eslint-disable-line no-self-assign
};
/* Language the UI is currently displayed in: Google cookie target (e.g. "hi") or the browser language when not translating. */
export const displayLanguage = () => {
  const m = document.cookie.match(/(?:^|;\s*)googtrans=\/en\/([a-zA-Z-]+)/);
  const saved = savedLanguage();
  if (m?.[1] && m[1] !== "en") return m[1];
  if (saved !== "en") return saved;
  return navigator.language || "en";
};
